import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type {
  EdpFormFieldMode,
  EdpPackageTemplate,
  EdpRequirementDetail,
  EdpReviewer,
  EdpTemplatesResponse,
} from '../../api/employeeDocsTypes.js';
import type { EdpSignatureMode } from '../../../shared/employeeDocs.js';
import {
  EDP_FIELD_FORMATS,
  EDP_FIELD_FORMAT_LABELS,
  type EdpFieldFormat,
} from '../../../shared/formValidation.js';
import { Modal } from '../../components/Modal.js';
import { ReviewerPicker } from './ReviewerPicker.js';
import {
  Alert,
  Button,
  Card,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../../components/ui.js';
import { FilePicker, type PickedFile } from '../../employeeDocs/shared.js';

export function EmployeeDocsTemplatesPage() {
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ['edp', 'templates'],
    queryFn: () => apiRequest<EdpTemplatesResponse>('/api/employee-documents/templates'),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['edp', 'templates'] });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiRequest(`/api/employee-documents/templates/requirements/${id}`, {
        method: 'PATCH',
        body: { isActive },
      }),
    onSuccess: () => void refresh(),
  });

  const [form, setForm] = useState({
    kind: 'form' as 'form' | 'credential',
    code: '',
    name: '',
    instructions: '',
    requiresSignature: true,
    signatureMode: 'digital' as EdpSignatureMode,
    minFiles: 1,
    maxFiles: 2,
    addToPackageTemplateId: '',
  });
  const [pdf, setPdf] = useState<PickedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [editingDefaults, setEditingDefaults] = useState<EdpPackageTemplate | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiRequest<{ id: string }>('/api/employee-documents/templates/requirements', {
        method: 'POST',
        body: {
          code: form.code,
          name: form.name,
          kind: form.kind,
          instructions: form.instructions || undefined,
          requiresSignature: form.kind === 'form' ? form.requiresSignature : false,
          signatureMode: form.kind === 'form' ? form.signatureMode : undefined,
          minFiles: form.minFiles,
          maxFiles: form.maxFiles,
          formPdfBase64: form.kind === 'form' ? pdf?.contentBase64 : undefined,
          addToPackageTemplateId: form.addToPackageTemplateId || undefined,
        },
      }),
    onSuccess: () => {
      setCreated(form.name);
      setForm((current) => ({ ...current, code: '', name: '', instructions: '' }));
      setPdf(null);
      setError(null);
      void refresh();
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The document could not be added.',
      ),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setCreated(null);
    if (form.kind === 'form' && !pdf) {
      setError('Choose the fillable PDF for this form.');
      return;
    }
    create.mutate();
  };

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const nameToCode = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60);

  return (
    <div>
      <PageHeader
        title="Required documents"
        subtitle="The forms an employee fills in and the credentials they upload. Forms are fillable PDFs; the fields are read from the PDF when it is added."
        actions={
          <Link
            to="/employee-documents"
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            Back to packages
          </Link>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Add a document">
          <form onSubmit={submit} className="flex flex-col gap-3">
            {error && <Alert variant="error">{error}</Alert>}
            {created && <Alert variant="success">Added “{created}”.</Alert>}
            <Field label="Type" htmlFor="t-kind" required>
              <Select
                id="t-kind"
                value={form.kind}
                onChange={(e) => set('kind', e.target.value as 'form' | 'credential')}
              >
                <option value="form">Form the employee fills in (fillable PDF)</option>
                <option value="credential">
                  Credential the employee uploads (ID, card, certificate)
                </option>
              </Select>
            </Field>
            <Field
              label="Name"
              htmlFor="t-name"
              required
              hint="Shown to the employee, reviewer and Contracting Officer."
            >
              <TextInput
                id="t-name"
                required
                value={form.name}
                onChange={(e) => {
                  set('name', e.target.value);
                  set('code', nameToCode(e.target.value));
                }}
              />
            </Field>
            <Field
              label="Code"
              htmlFor="t-code"
              required
              hint="Lower-case letters, digits and underscores; must be unique."
            >
              <TextInput
                id="t-code"
                required
                value={form.code}
                onChange={(e) => set('code', e.target.value)}
              />
            </Field>
            <Field label="Instructions to the employee" htmlFor="t-instructions">
              <TextArea
                id="t-instructions"
                rows={3}
                value={form.instructions}
                onChange={(e) => set('instructions', e.target.value)}
              />
            </Field>

            {form.kind === 'form' ? (
              <>
                <Field
                  label="Fillable PDF"
                  required
                  hint="Under 5 MB. The PDF must contain form fields; a scanned or flat PDF is refused."
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <FilePicker
                      label={pdf ? 'Choose a different PDF' : 'Choose PDF'}
                      accept="application/pdf"
                      onPick={(file) => setPdf(file)}
                    />
                    {pdf && <span className="text-sm text-slate-700">{pdf.fileName}</span>}
                  </div>
                </Field>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    checked={form.requiresSignature}
                    onChange={(e) => set('requiresSignature', e.target.checked)}
                  />
                  Employee must sign this form
                </label>
                {form.requiresSignature && (
                  <SignatureModeField
                    id="new-signature-mode"
                    value={form.signatureMode}
                    onChange={(value) => set('signatureMode', value)}
                  />
                )}
              </>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Minimum files"
                  htmlFor="t-min"
                  hint="2 for a card with a front and back."
                >
                  <TextInput
                    id="t-min"
                    type="number"
                    min={1}
                    max={10}
                    value={form.minFiles}
                    onChange={(e) => set('minFiles', Number(e.target.value))}
                  />
                </Field>
                <Field label="Maximum files" htmlFor="t-max">
                  <TextInput
                    id="t-max"
                    type="number"
                    min={1}
                    max={10}
                    value={form.maxFiles}
                    onChange={(e) => set('maxFiles', Number(e.target.value))}
                  />
                </Field>
              </div>
            )}

            <Field
              label="Add to package template"
              htmlFor="t-package"
              hint="Every new package built from that template will include this document."
            >
              <Select
                id="t-package"
                value={form.addToPackageTemplateId}
                onChange={(e) => set('addToPackageTemplateId', e.target.value)}
              >
                <option value="">Do not add to a template</option>
                {(templates.data?.packages ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </Select>
            </Field>

            <div>
              <Button type="submit" loading={create.isPending}>
                Add document
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Current documents">
          {templates.isLoading ? (
            <Spinner label="Loading" />
          ) : templates.isError || !templates.data ? (
            <Alert variant="error">The documents could not be loaded.</Alert>
          ) : (
            <div className="flex flex-col gap-4">
              {templates.data.packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="rounded-md bg-slate-50 p-3 text-sm ring-1 ring-slate-200"
                >
                  <p className="font-semibold text-slate-900">{pkg.name}</p>
                  {pkg.description && <p className="text-slate-600">{pkg.description}</p>}
                  <p className="mt-1 text-xs text-slate-500">
                    Template includes:{' '}
                    {pkg.requirementIds
                      .map(
                        (id) => templates.data.requirements.find((entry) => entry.id === id)?.name,
                      )
                      .filter(Boolean)
                      .join(', ') || 'nothing yet'}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-slate-600">
                      Default QA reviewers:{' '}
                      <span className="font-medium">
                        {pkg.defaultReviewerUserIds.length === 0
                          ? 'none'
                          : `${pkg.defaultReviewerUserIds.length} set`}
                      </span>{' '}
                      · Default Contracting Officer:{' '}
                      <span className="font-medium">
                        {pkg.defaultRecipientName
                          ? `${pkg.defaultRecipientName} (${pkg.defaultRecipientEmail})`
                          : 'none'}
                      </span>
                    </p>
                    <Button variant="secondary" onClick={() => setEditingDefaults(pkg)}>
                      Defaults
                    </Button>
                  </div>
                </div>
              ))}
              <ul className="divide-y divide-slate-200">
                {templates.data.requirements.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                  >
                    <div>
                      <p
                        className={
                          entry.isActive
                            ? 'font-medium text-slate-900'
                            : 'font-medium text-slate-400 line-through'
                        }
                      >
                        {entry.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {entry.code} ·{' '}
                        {entry.kind === 'form'
                          ? `form, ${entry.fieldCount ?? 0} fields${entry.requiresSignature ? (entry.signatureMode === 'ink' ? ', signed in ink' : ', e-signature required') : ''}`
                          : `upload, ${entry.minFiles === entry.maxFiles ? entry.minFiles : `${entry.minFiles}–${entry.maxFiles}`} file(s)`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="secondary" onClick={() => setConfiguring(entry.id)}>
                        Configure
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate({ id: entry.id, isActive: !entry.isActive })}
                      >
                        {entry.isActive ? 'Retire' : 'Reactivate'}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      {editingDefaults && (
        <PackageDefaultsModal
          template={editingDefaults}
          onClose={() => setEditingDefaults(null)}
          onSaved={() => {
            setEditingDefaults(null);
            void refresh();
          }}
        />
      )}

      {configuring && (
        <ConfigureRequirementModal
          id={configuring}
          packages={templates.data?.packages ?? []}
          onClose={() => setConfiguring(null)}
          onSaved={() => {
            setConfiguring(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

const MODE_LABELS: Record<EdpFormFieldMode, string> = {
  required: 'Required',
  optional: 'Optional',
  staff: 'Not for the employee',
};

interface FieldSetting {
  name: string;
  label: string;
  type: string;
  mode: EdpFormFieldMode;
  presetValue: string;
  format: EdpFieldFormat | '';
  section: string;
  instructions: string;
  options?: string[];
  choiceName?: string;
}

/**
 * Per-document settings: which package templates include it, whether it must be
 * signed, and — for a form — how each PDF field is treated for the employee.
 */
function ConfigureRequirementModal({
  id,
  packages,
  onClose,
  onSaved,
}: {
  id: string;
  packages: EdpPackageTemplate[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['edp', 'requirement', id],
    queryFn: () =>
      apiRequest<EdpRequirementDetail>(`/api/employee-documents/templates/requirements/${id}`),
  });

  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [signingStatement, setSigningStatement] = useState('');
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState<EdpSignatureMode>('digital');
  const [keepFillable, setKeepFillable] = useState(false);
  const [packageIds, setPackageIds] = useState<string[]>([]);
  const [fields, setFields] = useState<FieldSetting[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    setName(detail.data.name);
    setInstructions(detail.data.instructions ?? '');
    setSigningStatement(detail.data.signingStatement ?? '');
    setRequiresSignature(detail.data.requiresSignature);
    setSignatureMode(detail.data.signatureMode);
    setKeepFillable(detail.data.keepFillable);
    setPackageIds(detail.data.packageTemplateIds);
    setFields(
      (detail.data.fields ?? []).map((field) => ({
        name: field.name,
        label: field.label,
        type: field.type,
        mode: field.mode,
        presetValue: field.presetValue ?? '',
        format: field.format ?? '',
        section: field.section ?? '',
        instructions: field.instructions ?? '',
        options: field.options,
        choiceName: field.choiceName,
      })),
    );
  }, [detail.data]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/employee-documents/templates/requirements/${id}`, {
        method: 'PATCH',
        body: {
          name,
          instructions: instructions.trim() ? instructions : null,
          signingStatement:
            detail.data?.kind === 'form' ? signingStatement.trim() || null : undefined,
          requiresSignature: detail.data?.kind === 'form' ? requiresSignature : undefined,
          signatureMode: detail.data?.kind === 'form' ? signatureMode : undefined,
          keepFillable: detail.data?.kind === 'form' ? keepFillable : undefined,
          packageTemplateIds: packageIds,
          fields:
            detail.data?.kind === 'form'
              ? fields.map((field) => ({
                  name: field.name,
                  mode: field.mode,
                  label: field.label,
                  presetValue: field.type === 'text' ? field.presetValue.trim() || null : undefined,
                  format: field.type === 'text' ? field.format || null : undefined,
                  section: field.section.trim() || null,
                  instructions: field.instructions.trim() || null,
                }))
              : undefined,
        },
      }),
    onSuccess: onSaved,
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The changes could not be saved.',
      ),
  });

  const [rerendered, setRerendered] = useState<string | null>(null);
  const rerender = useMutation({
    mutationFn: () =>
      apiRequest<{ rerendered: number; skipped: number }>(
        `/api/employee-documents/templates/requirements/${id}/rerender`,
        { method: 'POST' },
      ),
    onSuccess: (result) => {
      setError(null);
      setRerendered(
        `Regenerated ${result.rerendered} completed cop${result.rerendered === 1 ? 'y' : 'ies'} from the saved answers` +
          (result.skipped ? `; ${result.skipped} not yet filled in were left alone.` : '.'),
      );
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The copies could not be regenerated.',
      ),
  });

  const [applied, setApplied] = useState<string | null>(null);
  const applyToExisting = useMutation({
    mutationFn: (packageTemplateId: string) =>
      apiRequest<{ updatedCount: number }>(
        `/api/employee-documents/templates/packages/${packageTemplateId}/apply-requirement`,
        { method: 'POST', body: { requirementTemplateId: id } },
      ),
    onSuccess: (result) => {
      setError(null);
      setApplied(
        result.updatedCount === 0
          ? 'Every existing package already includes this document.'
          : `Added to ${result.updatedCount} existing package(s); each employee has been emailed a new link.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['edp', 'packages'] });
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError
          ? cause.message
          : 'The document could not be added to existing packages.',
      ),
  });

  const setAll = (mode: EdpFormFieldMode) =>
    setFields((current) => current.map((field) => (field.choiceName ? field : { ...field, mode })));
  const counts = fields.reduce<Record<EdpFormFieldMode, number>>(
    (acc, field) => ({ ...acc, [field.mode]: acc[field.mode] + 1 }),
    { required: 0, optional: 0, staff: 0 },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={detail.data ? `Configure “${detail.data.name}”` : 'Configure document'}
      description="Choose which packages include this document and, for a form, which fields the employee must fill in. Fields marked “Not for the employee” are hidden from them and left blank for the requesting official."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!detail.data}>
            Save
          </Button>
        </>
      }
    >
      {detail.isLoading ? (
        <Spinner label="Loading document" />
      ) : detail.isError || !detail.data ? (
        <Alert variant="error">The document could not be loaded.</Alert>
      ) : (
        <div className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}
          <Field label="Name" htmlFor="c-name" required>
            <TextInput id="c-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Instructions to the employee" htmlFor="c-instructions">
            <TextArea
              id="c-instructions"
              rows={2}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </Field>
          <Field label="Included in package templates">
            <div className="flex flex-col gap-1">
              {packages.map((pkg) => (
                <label key={pkg.id} className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    checked={packageIds.includes(pkg.id)}
                    onChange={(e) =>
                      setPackageIds((current) =>
                        e.target.checked
                          ? [...current, pkg.id]
                          : current.filter((entry) => entry !== pkg.id),
                      )
                    }
                  />
                  {pkg.name}
                  {detail.data.packageTemplateIds.includes(pkg.id) && detail.data.isActive && (
                    <Button
                      type="button"
                      variant="ghost"
                      loading={applyToExisting.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Add “${detail.data?.name ?? ''}” to every existing “${pkg.name}” that does not have it yet? Packages already submitted or sent to USPS return to the employee for this one item; everything already completed is kept, and each employee is emailed a new link.`,
                          )
                        ) {
                          applyToExisting.mutate(pkg.id);
                        }
                      }}
                    >
                      Add to existing packages
                    </Button>
                  )}
                </label>
              ))}
            </div>
            {applied && <Alert variant="success">{applied}</Alert>}
          </Field>

          {detail.data.kind === 'form' && (
            <>
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={requiresSignature}
                  onChange={(e) => setRequiresSignature(e.target.checked)}
                />
                Employee must sign this form
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={keepFillable}
                  onChange={(e) => setKeepFillable(e.target.checked)}
                />
                <span>
                  Keep the completed copy fillable
                  <span className="block text-xs text-slate-500">
                    The PDF sent to the recipient keeps its form fields editable (the form's
                    original format) so they can add their own entries and signature; the employee's
                    e-signature is stamped and cannot be edited. Otherwise the copy is flattened.
                    Save, then use “Regenerate completed copies” to apply this to forms already
                    filled in.
                  </span>
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  disabled={rerender.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Regenerate the completed copy of this form in every package from the saved answers? Package statuses and USPS releases are unchanged; anyone with a link will see the new copy.',
                      )
                    ) {
                      rerender.mutate();
                    }
                  }}
                >
                  Regenerate completed copies
                </Button>
                {rerendered && <span className="text-sm text-emerald-700">{rerendered}</span>}
              </div>
              {requiresSignature && (
                <SignatureModeField
                  id="signature-mode"
                  value={signatureMode}
                  onChange={setSignatureMode}
                />
              )}
              {requiresSignature && (
                <Field
                  label="Shown at the point of signing"
                  htmlFor="c-signing"
                  hint="The form's own warning / certification wording, e.g. “I certify that the statements made by me…”."
                >
                  <TextArea
                    id="c-signing"
                    rows={4}
                    value={signingStatement}
                    onChange={(e) => setSigningStatement(e.target.value)}
                  />
                </Field>
              )}

              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-900">
                    Fields ({fields.length}) — {counts.required} required, {counts.optional}{' '}
                    optional, {counts.staff} not for the employee
                  </p>
                  <div className="flex gap-1 text-xs">
                    <Button variant="ghost" onClick={() => setAll('required')}>
                      All required
                    </Button>
                    <Button variant="ghost" onClick={() => setAll('optional')}>
                      All optional
                    </Button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto rounded-md ring-1 ring-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-2 py-1">Label shown to the employee</th>
                        <th className="px-2 py-1">Group</th>
                        <th className="px-2 py-1">Type</th>
                        <th className="px-2 py-1">Treatment</th>
                        <th className="px-2 py-1">Pre-filled value</th>
                        <th className="px-2 py-1">Must look like</th>
                        <th className="px-2 py-1">Directions shown to the employee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {fields.map((field, index) => (
                        <tr key={field.name}>
                          <td className="px-2 py-1">
                            <TextInput
                              aria-label={`Label for ${field.name}`}
                              value={field.label}
                              onChange={(e) =>
                                setFields((current) =>
                                  current.map((entry, i) =>
                                    i === index ? { ...entry, label: e.target.value } : entry,
                                  ),
                                )
                              }
                            />
                            <p className="truncate text-xs text-slate-400" title={field.name}>
                              {field.name}
                            </p>
                          </td>
                          <td className="px-2 py-1">
                            <TextInput
                              aria-label={`Group for ${field.name}`}
                              placeholder="Shown together"
                              value={field.section}
                              onChange={(e) =>
                                setFields((current) =>
                                  current.map((entry, i) =>
                                    i === index ? { ...entry, section: e.target.value } : entry,
                                  ),
                                )
                              }
                            />
                          </td>
                          <td className="px-2 py-1 text-slate-600">
                            {field.type === 'choice' ? 'pick one' : field.type}
                            {field.type === 'choice' && field.options ? (
                              <p className="text-xs text-slate-400">{field.options.join(' / ')}</p>
                            ) : null}
                          </td>
                          <td className="px-2 py-1">
                            {field.choiceName ? (
                              <span className="text-xs text-slate-500">
                                Ticked by “{fields.find((f) => f.name === field.choiceName)?.label}”
                              </span>
                            ) : (
                              <Select
                                aria-label={`Treatment for ${field.name}`}
                                value={field.mode}
                                onChange={(e) =>
                                  setFields((current) =>
                                    current.map((entry, i) =>
                                      i === index
                                        ? { ...entry, mode: e.target.value as EdpFormFieldMode }
                                        : entry,
                                    ),
                                  )
                                }
                              >
                                {(Object.keys(MODE_LABELS) as EdpFormFieldMode[]).map((mode) => (
                                  <option key={mode} value={mode}>
                                    {MODE_LABELS[mode]}
                                  </option>
                                ))}
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            {field.type === 'text' ? (
                              <TextInput
                                aria-label={`Pre-filled value for ${field.name}`}
                                placeholder="Printed on every copy"
                                value={field.presetValue}
                                onChange={(e) =>
                                  setFields((current) =>
                                    current.map((entry, i) =>
                                      i === index
                                        ? { ...entry, presetValue: e.target.value }
                                        : entry,
                                    ),
                                  )
                                }
                              />
                            ) : null}
                          </td>
                          <td className="px-2 py-1">
                            {field.type === 'text' ? (
                              <Select
                                aria-label={`Format for ${field.name}`}
                                value={field.format}
                                onChange={(e) =>
                                  setFields((current) =>
                                    current.map((entry, i) =>
                                      i === index
                                        ? {
                                            ...entry,
                                            format: e.target.value as EdpFieldFormat | '',
                                          }
                                        : entry,
                                    ),
                                  )
                                }
                              >
                                <option value="">Any text</option>
                                {EDP_FIELD_FORMATS.map((format) => (
                                  <option key={format} value={format}>
                                    {EDP_FIELD_FORMAT_LABELS[format]}
                                  </option>
                                ))}
                              </Select>
                            ) : null}
                          </td>
                          <td className="px-2 py-1">
                            {field.choiceName ? null : (
                              <TextArea
                                aria-label={`Directions for ${field.name}`}
                                placeholder="The form's directions for this question or group"
                                rows={2}
                                value={field.instructions}
                                onChange={(e) =>
                                  setFields((current) =>
                                    current.map((entry, i) =>
                                      i === index
                                        ? { ...entry, instructions: e.target.value }
                                        : entry,
                                    ),
                                  )
                                }
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function SignatureModeField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: EdpSignatureMode;
  onChange: (value: EdpSignatureMode) => void;
}) {
  return (
    <Field
      label="How the employee signs"
      htmlFor={id}
      hint={
        value === 'ink'
          ? 'The employee fills the form in online, downloads and prints it, signs in ink, then uploads the scan. The scan is what VGS reviews and USPS receives.'
          : 'The employee types their full name in the browser; it is stamped into the Signature box with the date.'
      }
    >
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value as EdpSignatureMode)}>
        <option value="digital">Electronically (type name to sign)</option>
        <option value="ink">By hand (print, sign in ink, upload the scan)</option>
      </Select>
    </Field>
  );
}

function PackageDefaultsModal({
  template,
  onClose,
  onSaved,
}: {
  template: EdpPackageTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const reviewers = useQuery({
    queryKey: ['edp', 'reviewers'],
    queryFn: () => apiRequest<EdpReviewer[]>('/api/employee-documents/reviewers'),
  });
  const [reviewerUserIds, setReviewerUserIds] = useState<string[]>(template.defaultReviewerUserIds);
  const [recipientName, setRecipientName] = useState(template.defaultRecipientName ?? '');
  const [recipientEmail, setRecipientEmail] = useState(template.defaultRecipientEmail ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/employee-documents/templates/packages/${template.id}`, {
        method: 'PATCH',
        body: {
          defaultReviewerUserIds: reviewerUserIds,
          defaultRecipientName: recipientName.trim() || null,
          defaultRecipientEmail: recipientEmail.trim() || null,
        },
      }),
    onSuccess: onSaved,
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The defaults could not be saved.',
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Defaults for “${template.name}”`}
      description="Pre-selected on every new package built from this template. They can still be changed on each package when it is created."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && <Alert variant="error">{error}</Alert>}
        <Field
          label="Default VGS QA reviewers"
          htmlFor="d-reviewer"
          hint="Any one of them can review and release a package; all are emailed when an employee submits."
        >
          <ReviewerPicker
            idPrefix="d-reviewer"
            reviewers={reviewers.data ?? []}
            selected={reviewerUserIds}
            onChange={setReviewerUserIds}
          />
        </Field>
        <Field label="Default Contracting Officer name" htmlFor="d-rname">
          <TextInput
            id="d-rname"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
          />
        </Field>
        <Field label="Default Contracting Officer email" htmlFor="d-remail">
          <TextInput
            id="d-remail"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
